import {AccountConstants, ClientModel, CurrencyConstants, IndexedDBCache} from "finhelper";
import {Subscription, from, timer} from "rxjs";
import {filter, switchMap, take} from "rxjs/operators";

import FilterAccount from "#app/domain/clients/FilterAccount";

/**
 * Лёгкий ответ без вложенных `accounts`: подставляем `[]`, чтобы downstream не падал на `.map`.
 */
function normalizeLightClientsAccounts(clients: any): unknown {
    if (!Array.isArray(clients)) {
        return clients;
    }

    return clients.map((client: any) => ({
        ...client,
        user_accounts: (client.user_accounts ?? []).map((ua: any) => ({
            ...ua,
            accounts: Array.isArray(ua.accounts) ? ua.accounts : [],
        })),
    }));
}

export class AllClientAccountsService {
    /**
     *
     * @type {null}
     */
    static jobSubscription: Subscription | null = null
    /**
     *
     * @type {boolean}
     * если идет запрос одних и тех же данных в разных местах, то чтобы не слать два запроса, а получить одним запросом, только один запрос получит с бека
     * и запишет в кэш, второй получит из кэша
     */
    static isLoading = false

    /**
     * Список клиентов с брокерскими счетами для указанного менеджера (WhiteSwan).
     * Сначала лёгкий запрос (только user_accounts), затем полный с вложенными счетами и блокировками.
     * В IndexedDB попадает только полный ответ; лёгкий отдаётся в Promise сразу.
     *
     * @param managerId id авторизованного пользователя (менеджера)
     * @param onAccountsEnriched колбэк после полной подгрузки (для повторного merge с инвест-сеткой)
     * @return {Promise<any>}
     */
    static do = async (managerId: string | number, onAccountsEnriched?: (data: any) => void) => {
        //запоминаем навсегда, но если уже прошло 24 часа показываем дате последней загрузки и обновляем данные до текущей
        const rememberTime = 1000 * 60 * 60 * 24;//1000 дней
        const cacheKey = `all_clients_accounts__manager-${managerId}`;

        return new Promise((resolve, reject) => {
            void (async () => {
                try {
                    const data = await IndexedDBCache.get(cacheKey);

                    if (!data && !this.isLoading) {
                        this.isLoading = true;

                        const runHeavyFetch = () => {
                            ClientModel.fetch({currency_id: CurrencyConstants.RUBBLE_ID})
                                .setDomain(process.env.REACT_APP_API_WHITESWAN_URL)
                                .where('manager_id', managerId)
                                .with('user_accounts', (query: any) => {
                                    return query
                                        .with('accounts')
                                        .with('accounts.blocked')
                                        .with('accounts.blocked.operation')
                                        .with('accounts.blocked.operation.active_relation')
                                        .with('accounts.blocked.operation.active_relation.buy_trades')
                                        .with('accounts.currency')
                                        .with('accounts.currency.cb_currency')
                                        .where('type_id', AccountConstants.BROKER_ACCOUNT)
                                        .whereIn('bank_id', FilterAccount.ACCOUNT_BANKS);
                                })
                                .all(async ({data: heavy}: {data: any }) => {
                                    try {
                                        await IndexedDBCache.set(cacheKey, heavy, rememberTime);
                                        onAccountsEnriched?.(heavy);
                                    } finally {
                                        this.isLoading = false;
                                    }
                                });
                        };

                        ClientModel.fetch({currency_id: CurrencyConstants.RUBBLE_ID})
                            .setDomain(process.env.REACT_APP_API_WHITESWAN_URL)
                            .where('manager_id', managerId)
                            .with('user_accounts', (query: any) => {
                                return query
                                    .where('type_id', AccountConstants.BROKER_ACCOUNT)
                                    .whereIn('bank_id', FilterAccount.ACCOUNT_BANKS);
                            })
                            .all(async ({data: light}: {data: any }) => {
                                try {
                                    resolve(normalizeLightClientsAccounts(light));
                                    runHeavyFetch();
                                } catch (e) {
                                    this.isLoading = false;
                                    reject(e);
                                }
                            });
                    } else if (this.isLoading) {
                        this.jobSubscription?.unsubscribe();
                        this.jobSubscription = timer(0, 1000)
                            .pipe(
                                switchMap(() => from(IndexedDBCache.get(cacheKey))),
                                filter(Boolean),
                                take(1),
                            )
                            .subscribe((polled) => {
                                this.jobSubscription?.unsubscribe();
                                this.jobSubscription = null;
                                onAccountsEnriched?.(polled);
                                resolve(polled);
                            });
                    } else {
                        onAccountsEnriched?.(data);
                        resolve(data);
                    }
                } catch (e) {
                    reject(e);
                }
            })();
        });
    }

    static async clear() {
        this.isLoading = false;
        this.jobSubscription?.unsubscribe();
        this.jobSubscription = null;
        await IndexedDBCache.delete('all_actives')
    }
}
